import {
  useState,
  useEffect,
  useRef,
  // useMemo
} from "react";
import { styled as muiStyled } from "@mui/system";
import styled from "styled-components";
import { useNavigate, useParams } from "react-router-dom";

import { Stepper, Step, StepButton, Stack } from "@mui/material";
import Button from "src/components/core/Button";

import Loading from "src/components/ui/Loading";

import Step1 from "./Step1";
import Step2 from "./Step2";
import Step3 from "./Step3";

import { useSteps } from "src/hooks/useStep";
import { useForm, useFieldArray } from "react-hook-form";
import * as yup from "yup";
import useYupValidationResolver from "src/hooks/useYupValidationResolver";
import {
  createBlogByAdmin,
  getBlogDetail,
  updateBlogByAdmin,
} from "src/api/blog";

import { validFileExtensions } from "src/common/file";

import { useSnackbar } from "notistack";

const S = {};

S.ButtonGroup = styled(Stack)`
  position: fixed;
  width: 100%;
  max-width: 1240px;
  bottom: 0;
  height: 72px;
  background-color: #fff;
  z-index: 15;
  margin-top: 24px;
`;

S.Container = styled.div`
  width: 100%;
  max-width: 1240px;
  position: relative;
  padding: 24px;
  padding-bottom: 75px;
  margin: auto;

  & > div > p {
    word-wrap: break-word;
  }
`;

const drawerWidth = 260;

const Main = muiStyled("main", {
  shouldForwardProp: (prop) => prop !== "open" && prop !== "sx",
})(({ theme, open }) => ({
  position: "relative",
  flexGrow: 1,
  // padding: theme.spacing(3),
  transition: theme.transitions.create("margin", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  marginLeft: `-${drawerWidth}px`,
  ...(open && {
    transition: theme.transitions.create("margin", {
      easing: theme.transitions.easing.easeOut,
      duration: theme.transitions.duration.enteringScreen,
    }),
    marginLeft: 0,
  }),
  marginTop: "75px",
  height: "calc(100vh - 75px)",
  overflow: "hidden",
  overflowY: "scroll",
  scrollBehavior: "smooth",
}));

const steps = ["ข้อมูลที่แสดงบนการ์ด", "เนื้อหาบทความ", "สำเร็จ"];

const validationSchema = yup.object().shape({
  banner_image: yup.mixed().required(),
  preview_image: yup.mixed().required(),
  title: yup.string().required(),
  sub_title: yup.string().required(),
  hashtags: yup.array().of(yup.object()).min(1).required(),
  product_choice_blogs: yup.array().of(yup.object()).min(1).required(),
  belong_to: yup.string().required(),
  contents: yup
    .array()
    .of(
      yup.object({
        group_media: yup.array().of(
          yup.object({
            media: yup.mixed(),
            link: yup.string(),
            hotspot: yup.array().of(
              yup.object({
                pc_id: yup.object().required(),
                position_x: yup.number().required(),
                position_y: yup.number().required(),
              }),
            ),
          }),
        ),
        content_text: yup.string(),
        placement: yup
          .string()
          .oneOf(["1-square", "2-square", "1-landscape"])
          .required(),
      }),
    )
    .min(1)
    .test({
      name: "required",
      message: "Paragraphs should contain either media/text.",
      test: (array) =>
        array.every(({ group_media, content_text }, index) => {
          return (
            group_media.every(({ media, link }) => !!media || !!link) ||
            !!content_text
          );
        }),
    })
    .test({
      name: "required",
      message: "The amount of media must be equal to the selected layout.",
      test: (array) =>
        array.every(({ group_media, placement }) => {
          if (
            parseInt(placement.charAt(0)) === 2 &&
            group_media.filter((item) => item.media || item.link).length === 1
          ) {
            return false;
          }
          return true;
        }),
    })
    .required(),
});

// export const MAX_CONTENT_PARAGRAPH = 3;
export const MAX_PRODUCT_PER_BLOG = 30;
export const MAX_SPOT_PER_IMAGE = 5;

const CreateBlog = ({ open }) => {
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const { blogId } = useParams();
  const { currentStep, goToNextStep, goToPrevStep } = useSteps({
    initialStep: 0,
    maxStep: 2,
  });

  const defaultValues = {
    banner_image: null,
    preview_image: null,
    title: "",
    sub_title: "",
    hashtags: [],
    product_choice_blogs: [],
    contents: [
      {
        group_media: [
          {
            media: "",
            link: "",
            hotspot: [],
          },
        ],
        content_text: "",
        placement: "1-square",
      },
    ],
    belong_to: "",
  };

  const resolver = useYupValidationResolver(validationSchema);
  const {
    formState: { errors: formErrors },
    control: formControl,
    watch,
    trigger,
    getValues,
    setValue: setFormValue,
    reset,
  } = useForm({
    resolver,
    defaultValues,
  });

  // console.log("formErrors", formErrors);

  /**
   * Check if the form errors object lists these fields.
   * If yes, the form status is not yet valid.
   * */
  const isFirstStepValid = [
    "banner_image",
    "preview_image",
    "title",
    "sub_title",
    "hashtags",
    "belong_to",
  ].every((required) => !Object.keys(formErrors).includes(required));

  const isSecondStepValid = ["product_choice_blogs", "contents"].every(
    (required) =>
      Object.keys(formErrors).every((key) => !key.includes(required)),
  );

  // console.log("isFirstStepValid", isFirstStepValid);
  // console.log("isSecondStepValid", isSecondStepValid);

  // console.log("Object.keys(formErrors)", Object.keys(formErrors));

  const { insert: appendElement, remove: removeElement } = useFieldArray({
    control: formControl,
    name: "contents",
  });

  const [formData, setFormData] = useState(defaultValues);
  const [isLoading, setIsLoading] = useState(false);

  // Edit
  useEffect(() => {
    if (blogId === undefined) return;

    (async () => {
      await getBlogDetail(blogId)
        .then(async ({ data }) => {
          if (data) reset(data);
        })
        .catch((err) => {
          console.error("error: ", err.response.data.message);
          navigate("/404");
        });
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [blogId]);

  /**
   * @notice after form value change, then set value to formData state
   */
  useEffect(() => {
    const subscription = watch((values, { name, type }) => {
      setFormData(values);
    });
    return () => subscription.unsubscribe();
  }, [watch]);

  /**
   * @notice trigger validation when formData updated
   */
  useEffect(() => {
    trigger();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData]);

  useEffect(() => {
    if (isLoading) document.body.style.overflow = "hidden";
    else document.body.style = "none";
  }, [isLoading]);

  const isAllowedMedia = (file) => {
    const allowedExtensions = ["png", "jpg", "jpeg"];

    if (!validFileExtensions(file, allowedExtensions)) {
      enqueueSnackbar(
        `File type is not allowed. Only accept ${allowedExtensions.join(
          ", ",
        )}.`,
        { variant: "error" },
      );
      return false;
    } else if (file.size > 2428800) {
      enqueueSnackbar(`File size is too large. Max file size is 2MB`, {
        variant: "error",
      });
      return false;
    }

    return true;
  };

  const handleChange = async (event, replaceName, replaceValue) => {
    // Helper: Check if the provided value is a valid media file
    const isValidMedia = (val) =>
      typeof val === "object" && val !== null && !isAllowedMedia(val);
    // Helper: Convert hashtag values to the required object format
    const mapHashtags = (value) =>
      value.map((props) =>
        typeof props === "string" ? { name: props } : { name: props.name },
      );
    // Helper: Update group_media array when the layout (placement) changes
    const getUpdatedMediaForPlacement = (editingRow, value) => {
      const currentLayoutMedia = parseInt(editingRow.placement.charAt(0));
      const newLayoutMedia = parseInt(value.charAt(0));
      const attachedMedia = editingRow.group_media.filter(
        (item) => item.media,
      ).length;
      if (newLayoutMedia > currentLayoutMedia) {
        // If the new layout requires more media slots, add empty objects to fill the difference
        return [
          ...editingRow.group_media,
          ...[...new Array(newLayoutMedia - attachedMedia)].map(() => ({
            media: "",
            link: "",
            hotspot: [],
          })),
        ];
      } else {
        // If the new layout requires fewer media slots, keep only the required amount, prioritizing non-empty slots
        // eslint-disable-next-line array-callback-return
        let sortedNotEmptyMediaSlot = editingRow.group_media.sort((a, b) => {
          if (a.media === b.media) return 0;
          else if (a.media === "") return 1;
          else if (b.media === "") return -1;
        });
        return sortedNotEmptyMediaSlot.slice(
          0,
          Math.max(1, Math.min(currentLayoutMedia - 1, attachedMedia - 1)),
        );
      }
    };
    // Helper: Reset group_media to a single empty object
    const resetGroupMedia = () => [{ media: "", link: "", hotspot: [] }];

    let name =
      typeof replaceName !== "undefined" ? replaceName : event.target.name;
    let value =
      typeof replaceValue !== "undefined" ? replaceValue : event.target.value;
    let path = name.split(".");
    let [key, ...rest] = path;
    let editingKey = key;
    let updated;

    if (key === "title" || key === "sub_title") {
      // Handle simple fields (title, sub_title)
      updated = value;
    } else if (key === "banner_image" || key === "preview_image") {
      // Handle image fields (banner_image, preview_image)
      if (isValidMedia(value)) return;
      updated = value;
    } else if (key === "hashtags") {
      // Handle hashtags: ensure each is an object with a name property
      updated = mapHashtags(value);
    } else if (key === "product_choice_blogs") {
      // Handle product choices: enforce maximum allowed
      if (value.length > MAX_PRODUCT_PER_BLOG) {
        enqueueSnackbar(
          `Max product choice per blog is ${MAX_PRODUCT_PER_BLOG} items`,
          { variant: "error" },
        );
        return;
      }
      updated = value;
    } else if (key === "contents") {
      // Handle nested content fields
      let [objIndex, objKey, subObjIndex, subObjKey, thirdLevelObjIndex] = rest;
      editingKey = `${key}.${objIndex}`;
      let editingRow = getValues(editingKey);
      if (objKey === "content_text") {
        // Update content text for a paragraph
        updated = { ...editingRow, content_text: value };
      } else if (objKey === "placement") {
        // Handle layout (placement) changes for a paragraph
        if (value === editingRow.placement) return;
        /*
         * When the layout changes, adjust the group_media array:
         * - If the new layout requires more media, add empty slots.
         * - If it requires fewer, keep only the necessary slots, prioritizing non-empty ones.
         * This allows users to switch layouts without losing their media, as long as it fits the new layout's requirements.
         */
        updated = {
          ...editingRow,
          placement: value,
          group_media: getUpdatedMediaForPlacement(editingRow, value),
        };
      } else if (objKey === "group_media") {
        // Handle updates to group_media (media, link, hotspot, or deletion)
        if (subObjKey === "media" || subObjKey === "link") {
          // Update a specific media or link in group_media
          if (subObjKey === "media" && value !== null && !isAllowedMedia(value))
            return;
          updated = {
            ...editingRow,
            group_media: editingRow.group_media.map((item, index) =>
              index === parseInt(subObjIndex)
                ? { ...item, [subObjKey]: value }
                : item,
            ),
          };
        } else if (subObjKey === "hotspot") {
          // Update a specific hotspot in group_media
          updated = {
            ...editingRow,
            group_media: editingRow.group_media.map((item, index) =>
              index === parseInt(subObjIndex)
                ? {
                    ...item,
                    hotspot: item.hotspot.map((spot, spotIndex) =>
                      spotIndex === parseInt(thirdLevelObjIndex)
                        ? { ...spot, pc_id: value }
                        : spot,
                    ),
                  }
                : item,
            ),
          };
        } else {
          // Handle deletion: if only one media remains, reset to default; otherwise, clear the selected slot
          const remainingMedia = editingRow.group_media.filter(
            (item) => item.media || item.link,
          ).length;
          if (remainingMedia === 1) {
            // If only one media remains and it's being deleted, reset to default layout and empty media
            updated = {
              ...editingRow,
              placement: "1-square",
              group_media: resetGroupMedia(),
            };
          } else if (remainingMedia > 1) {
            // If more than one media remains, clear the selected slot
            updated = {
              ...editingRow,
              group_media: editingRow.group_media.map((item, index) =>
                index === parseInt(subObjIndex)
                  ? { media: "", link: "", hotspot: [] }
                  : item,
              ),
            };
          }
        }
      }
      // If contents but not a recognized objKey, do nothing
    } else if (key === "belong_to") {
      // Handle belong_to field
      updated = value;
    } else {
      // Default: set value if key is not recognized
      updated = value;
    }
    setFormValue(editingKey, updated);
  };

  function getStepComponent() {
    switch (currentStep) {
      case 0:
        return (
          <Step1
            formErrors={formErrors}
            formData={formData}
            onChange={handleChange}
          />
        );
      case 1:
        return (
          <Step2
            formControl={formControl}
            formErrors={formErrors}
            formData={formData}
            onChange={handleChange}
            onAppendElement={(index) => {
              // if (formData.contents.length === MAX_CONTENT_PARAGRAPH) return;
              appendElement(index, {
                group_media: [
                  {
                    media: "",
                    link: "",
                    hotspot: [],
                  },
                ],
                content_text: "",
                placement: "1-square",
              });
            }}
            onRemoveElement={removeElement}
            onReArrangeParagraph={reArrangeParagraph}
          />
        );
      case 2:
        return (
          <Step3
            formControl={formControl} // just for prevent error formControl is not defined in spottable image
            formData={formData}
          />
        );
      default:
        return null;
    }
  }

  const handleSave = async (isDraft) => {
    setIsLoading(true);
    let data = new FormData();
    const createBlogParams = {
      ...formData,
      product_choice_blogs: formData?.product_choice_blogs.map(
        (product) => product._id,
      ),
      view_count: 0,
      is_draft: isDraft, // just in case, if isDraft is true, then set visibility to false
    };

    Object.keys(createBlogParams).forEach((key) => {
      if (key === "contents") {
        let contentJson = [];
        createBlogParams[key].map((content, index) => {
          const new_group_media = content.group_media.map(
            ({ media, ...rest }, i) => {
              data.append(`${key}[${index}]group_media[${i}]`, media);
              return blogId !== undefined
                ? typeof media === "string"
                  ? { ...rest, media }
                  : { ...rest, media: "" }
                : rest;
            },
          );

          return contentJson.push({
            ...content,
            group_media: new_group_media,
          });
        });

        data.append(key, JSON.stringify(contentJson));
      } else if (Array.isArray(createBlogParams[key])) {
        data.append(key, JSON.stringify(createBlogParams[key]));
      } else {
        data.append(key, createBlogParams[key]);
      }
      return data;
    });

    if (blogId !== undefined) {
      // setIsLoading(false);
      // for (var pair of data.entries()) {
      //   console.log(pair[0]+ ', ' + pair[1]);
      // }

      await updateBlogByAdmin(data)
        .then((res) => {
          setIsLoading(false);
          enqueueSnackbar("Update Success!", { variant: "success" });
          if (isDraft) return;
          setTimeout(() => {
            navigate("/blog/list");
          }, 1500);
        })
        .catch((err) => {
          setIsLoading(false);
          enqueueSnackbar(err.response.data.message, { variant: "error" });
        });
    } else {
      await createBlogByAdmin(data)
        .then((res) => {
          setIsLoading(false);
          enqueueSnackbar("Create Success!", { variant: "success" });
          if (isDraft) return;
          setTimeout(() => {
            navigate("/blog/list");
          }, 1500);
        })
        .catch((err) => {
          setIsLoading(false);
          enqueueSnackbar(err.response.data.message, { variant: "error" });
        });
    }
  };

  const reArrangeParagraph = (fromIndex, toIndex) => {
    const content = formData.contents[fromIndex];
    const newContents = formData.contents.filter(
      (_, index) => index !== fromIndex,
    );
    newContents.splice(toIndex, 0, content);
    setFormValue("contents", newContents);

    setTimeout(() => {
      document.getElementById(`content-${toIndex}`).scrollIntoView({
        behavior: "smooth",
      });
    }, 100);
  };

  const scrollRef = useRef(null);

  const scrollToTop = () => {
    scrollRef.current.scrollTo(0, 0);
  };

  useEffect(() => {
    scrollToTop();
  }, [currentStep]);

  return (
    <>
      <Main
        sx={{
          width: "100%",
          background: "#fff",
        }}
        open={open}
        ref={scrollRef}
      >
        <S.Container>
          <Stack
            width={"100%"}
            alignItems={"center"}
            sx={{ position: "relative" }}
          >
            <Stepper
              activeStep={currentStep}
              sx={{
                ".MuiStepLabel-root": { display: "grid", maxWidth: "200px" },
                ".MuiStepIcon-root": { margin: "0 auto 12px auto" },
                ".MuiStepIcon-root.Mui-active": {
                  color: "#e04132",
                },
                ".MuiStepIcon-root.Mui-completed": {
                  color: "#e04132",
                },
                ".MuiStepLabel-label.Mui-active": {
                  color: "#e04132",
                },
                width: 500,
                marginBottom: 3,
              }}
              alternativeLabel
            >
              {steps.map((label) => (
                <Step key={label}>
                  <StepButton disabled={true} color="inherit">
                    {label}
                  </StepButton>
                </Step>
              ))}
            </Stepper>
            {getStepComponent()}
            <S.ButtonGroup
              flexDirection={"row"}
              justifyContent={"center"}
              alignItems={"center"}
              columnGap={2}
            >
              <Button
                variant="outlined"
                size="large"
                onClick={() => (currentStep !== 0 ? goToPrevStep() : null)}
                disabled={currentStep === 0}
                sx={{ minWidth: 140 }}
              >
                ย้อนกลับ
              </Button>
              <Button
                variant="contained"
                size="large"
                onClick={() =>
                  currentStep !== 2 ? goToNextStep() : handleSave()
                }
                disabled={
                  currentStep === 0
                    ? !isFirstStepValid
                    : currentStep === 1
                      ? !isSecondStepValid
                      : isFirstStepValid && !isSecondStepValid
                }
                sx={{ minWidth: 140 }}
              >
                {currentStep !== 2 ? "ถัดไป" : "บันทึก"}
              </Button>
              <Button
                variant="text"
                size="large"
                onClick={() => handleSave(true)}
                sx={{ minWidth: 140 }}
              >
                บันทึกร่าง
              </Button>
            </S.ButtonGroup>
          </Stack>
        </S.Container>
      </Main>

      {isLoading && <Loading />}
    </>
  );
};

export default CreateBlog;
